import React from 'react';
import { ChakraProvider, Box } from '@chakra-ui/react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import JournalPage from './pages/journal/JournalPage';
import TagsPage from './pages/tags/TagsPage';
import HabitsPage from './pages/habits/HabitsPage';
import AnalyticsPage from './pages/analytics/AnalyticsPage';
import ChatPage from './pages/chat/ChatPage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ProfilePage from './pages/profile/ProfilePage';
import SettingsPage from './pages/settings/SettingsPage';
import LandingPage from './pages/LandingPage';
import PricingPage from './pages/PricingPage';
import PrivacyPolicyPage from './pages/legal/PrivacyPolicyPage';
import TermsOfServicePage from './pages/legal/TermsOfServicePage';
import ContactPage from './pages/legal/ContactPage';
import { JournalProvider } from './contexts/JournalContext';
import { TagProvider } from './contexts/TagsContext';
import { MediaProvider } from './contexts/MediaContext';
import { LocationProvider } from './contexts/LocationContext';
import { SearchProvider } from './contexts/SearchContext';
import { HabitProvider } from './contexts/HabitContext';
import { AnalyticsProvider } from './contexts/AnalyticsContext';
import { AIProvider } from './contexts/AIContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CustomFieldsProvider } from './contexts/CustomFieldsContext';
import Layout from './components/Layout';
import { EncryptionProvider } from 'contexts/EncryptionContext';
import DashboardPage from './pages/dashboard/DashboardPage';
import RecoveryPage from 'pages/auth/RecoveryPage';
import { SubscriptionProvider } from 'contexts/SubscriptionContext';

// Protected route wrapper component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();

  // Show nothing while checking authentication
  if (loading) {
    return null;
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <ChakraProvider>
      <AuthProvider>
        <Router>
          <Layout>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/recovery" element={<RecoveryPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/terms" element={<TermsOfServicePage />} />
              <Route path="/contact" element={<ContactPage />} />

              {/* Protected Routes */}
              <Route
                path="/app/*"
                element={
                  <ProtectedRoute>
                    <EncryptionProvider>
                      <SubscriptionProvider>
                        <JournalProvider>
                          <TagProvider>
                            <MediaProvider>
                              <LocationProvider>
                                <SearchProvider>
                                  <HabitProvider>
                                    <AnalyticsProvider>
                                      <AIProvider>
                                        <CustomFieldsProvider>
                                          <Box display="flex" minH="100vh">
                                            <Sidebar />
                                            <Box flex="1" p={0} overflow="auto">
                                              <Routes>
                                                <Route path="/" element={<Navigate to="/app/dashboard" replace />} />
                                                <Route path="/dashboard" element={<DashboardPage />} />
                                                <Route path="/journal" element={<JournalPage />} />
                                                <Route path="/tags" element={<TagsPage />} />
                                                <Route path="/habits" element={<HabitsPage />} />
                                                <Route path="/analytics" element={<AnalyticsPage />} />
                                                <Route path="/chat" element={<ChatPage />} />
                                                <Route path="/profile" element={<ProfilePage />} />
                                                <Route path="/settings" element={<SettingsPage />} />
                                                <Route path="/pricing" element={<PricingPage />} />
                                                <Route path="*" element={<Navigate to="/app/dashboard" replace />} />
                                              </Routes>
                                            </Box>
                                          </Box>
                                        </CustomFieldsProvider>
                                      </AIProvider>
                                    </AnalyticsProvider>
                                  </HabitProvider>
                                </SearchProvider>
                              </LocationProvider>
                            </MediaProvider>
                          </TagProvider>
                        </JournalProvider>
                      </SubscriptionProvider>
                    </EncryptionProvider>
                  </ProtectedRoute>
                }
              />

              {/* Catch all redirect to landing */}
              <Route path="*" element={
                <ProtectedRoute>
                  <Navigate to="/app/dashboard" replace />
                </ProtectedRoute>
              } />
            </Routes>
          </Layout>
        </Router>
      </AuthProvider>
    </ChakraProvider>
  );
}

export default App;
